// Números de la sala con formato de España (punto de miles, coma decimal) y comparaciones con flecha.
import { t } from '../../../i18n';
import type { ClaveMetrica } from './tipos';

const nf = (decimales: number) =>
  // de-DE: mismos separadores que España, pero agrupa también los miles de 4 cifras (1.234; es-ES pone 1234).
  new Intl.NumberFormat('de-DE', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });

/** Lo que se pinta cuando no hay dato: nunca un número inventado. */
export const SIN_VALOR = '—';

export function numero(x: number, decimales = 0): string {
  return nf(decimales).format(x);
}

/** Miles para un eje: «2», «2,5», «40» (con decimal solo si hace falta: una marca de 2.500 no es «3»). */
export function miles(x: number): string {
  return numero(x / 1000, x % 1000 ? 1 : 0);
}

export function dinero(x: number, decimales = 0): string {
  return `${numero(x, decimales)} $`;
}

/** Dólares exactos para la lectura al pasar el ratón: con céntimos por debajo de 10.000 $ (152,34 $; 12.345 $). */
export function dineroExacto(x: number): string {
  return dinero(x, Math.abs(x) < 10_000 ? 2 : 0);
}

/** Cómo se enseña el valor de cada métrica. */
export function valorMetrica(clave: ClaveMetrica, x: number | null): string {
  if (x === null) return SIN_VALOR;
  switch (clave) {
    case 'unlock':
    case 'cvr':
    case 'golden':
      return `${numero(x, 1)} %`;
    case 'reply':
      return `${numero(x)} s`;
    case 'por_fan':
      return dinero(x, 2);
    case 'por_msg':
      return dinero(x, 3);
  }
}

/** Las métricas en % se comparan en puntos; el resto, en % de cambio. */
export const EN_PUNTOS: Record<ClaveMetrica, boolean> = {
  unlock: true,
  cvr: true,
  golden: true,
  reply: false,
  por_fan: false,
  por_msg: false,
};

/** Si subir es bueno (el reply time, al revés). */
export const SUBIR_ES_BUENO: Record<ClaveMetrica, boolean> = {
  unlock: true,
  cvr: true,
  golden: true,
  reply: false,
  por_fan: true,
  por_msg: true,
};

export interface Delta {
  direccion: 'sube' | 'baja' | 'igual';
  tono: 'bueno' | 'malo' | 'neutro';
  /** Magnitud ya formateada, sin signo: «15 %» o «0,5 pts». */
  texto: string;
}

/** Diferencia entre hoy y una referencia. Por debajo del umbral se considera igual (no se pinta ruido). */
export function delta(hoy: number | null, ref: number | null, enPuntos: boolean, subirEsBueno = true): Delta | null {
  if (hoy === null || ref === null) return null;
  if (!enPuntos && ref === 0) return null;
  const d = enPuntos ? hoy - ref : ((hoy - ref) / ref) * 100;
  const umbral = enPuntos ? 0.05 : 1.5;
  if (Math.abs(d) < umbral) return { direccion: 'igual', tono: 'neutro', texto: '' };
  const sube = d > 0;
  return {
    direccion: sube ? 'sube' : 'baja',
    tono: sube === subirEsBueno ? 'bueno' : 'malo',
    texto: enPuntos ? `${numero(Math.abs(d), 1)} pts` : `${numero(Math.abs(d))} %`,
  };
}

/** «20 s», «5 min», «2 h». */
export function edad(segundos: number): string {
  if (segundos < 90) return `${Math.max(0, Math.round(segundos))} s`;
  if (segundos < 90 * 60) return `${Math.round(segundos / 60)} min`;
  if (segundos < 48 * 3600) return `${Math.round(segundos / 3600)} h`;
  return t('salaControl.dias', { n: Math.round(segundos / 86400) });
}

/** Zona de todas las horas de la Sala de Control: UTC, la misma del «día» del panel y del CRM (Pau, 06-10:
 *  «lo quiero en UTC todo siempre»; antes salían en hora de España sin decirlo). */
export const ZONA_SALA = 'UTC';

/** «HH:MM» (UTC) de la hora UTC decimal `h` del día UTC que contiene `referencia`. */
export function horaUtc(h: number, referencia: Date = new Date()): string {
  const inicio = Date.UTC(referencia.getUTCFullYear(), referencia.getUTCMonth(), referencia.getUTCDate());
  return new Date(inicio + h * 3_600_000).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: ZONA_SALA });
}

/** Eje vertical con marcas redondas (Pau 10-10: «0/10/20/30/40 s», no 13/25/38): un paso de 1, 2, 2,5 o 5 por potencia
 *  de 10 que deja unas `divisiones` marcas, y el tope en la primera marca por encima del máximo (+5 % de aire). */
export function escalaRedonda(max: number, divisiones = 5): { tope: number; marcas: number[] } {
  if (!(max > 0)) return { tope: 1, marcas: [0, 0.5, 1] };
  const objetivo = max * 1.05;
  const bruto = objetivo / divisiones;
  const potencia = 10 ** Math.floor(Math.log10(bruto));
  const paso = [1, 2, 2.5, 5, 10].map((f) => f * potencia).find((p) => p >= bruto) ?? 10 * potencia;
  const n = Math.ceil(objetivo / paso - 1e-9);
  const marcas = Array.from({ length: n + 1 }, (_, i) => Number((i * paso).toPrecision(12)));
  return { tope: marcas[n]!, marcas };
}

/** Horas rotuladas en un eje de 24 h de `anchoUtil` px: cada 3 h si caben («00:00» pide ~46 px), si no cada 6 h. */
export function horasEje(anchoUtil: number): number[] {
  const cada = anchoUtil / 8 >= 46 ? 3 : 6;
  return Array.from({ length: 24 / cada + 1 }, (_, i) => i * cada);
}

/** % enteros que suman 100 (resto mayor): 91,5 + 8,5 no puede salir «92 % + 9 %». */
export function enteros100(pcts: number[]): number[] {
  const base = pcts.map(Math.floor);
  const faltan = Math.round(pcts.reduce((s, x) => s + x, 0)) - base.reduce((s, x) => s + x, 0);
  const orden = pcts.map((x, i) => [x - Math.floor(x), i] as const).sort((a, b) => b[0] - a[0]);
  for (let k = 0; k < faltan && k < orden.length; k++) base[orden[k]![1]]! += 1;
  return base;
}

/** Horas sueltas → tramos seguidos [desde, hasta) («13, 14, 15, 16» → [[13, 17]]). */
export function tramosDeHoras(horas: readonly number[]): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const h of [...horas].sort((a, b) => a - b)) {
    const ult = out[out.length - 1];
    if (ult && ult[1] === h) ult[1] = h + 1;
    else out.push([h, h + 1]);
  }
  return out;
}
